import cn from 'classnames';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { templatesApi } from '@/shared/api/transcriptor/templatesApi';
import { toastErrors } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Input, Modal, toastCaller } from '@/shared/ui';

import { registryAssetsModel } from '@/entities/registryAssets';
import { templatesModel } from '@/entities/templates';

import { i18n, REGISTRY_SELECTOR_PAGE_SIZE } from '../../config';
import { RegistryAssetSelectorCard } from '../RegistryAssetSelectorCard/RegistryAssetSelectorCard';

import styles from './RegistryAssetSelectorDialog.module.scss';

export type RegistryAssetFamily =
  | 'document_template'
  | 'encounter_schema'
  | 'analytics_model';

export type RegistryAssetImportResult = {
  family: RegistryAssetFamily;
  document_template_id?: string;
  document_template_series_id?: string;
};

type RegistryAssetSelectorDialogProps = {
  isOpen: boolean;
  family: RegistryAssetFamily;
  organizationId?: string;
  language?: string;
  onSelect: (result: RegistryAssetImportResult) => void;
  onClose: () => void;
};

export const RegistryAssetSelectorDialog: FC<
  RegistryAssetSelectorDialogProps
> = (props) => {
  const { isOpen, family, organizationId, language, onSelect, onClose } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const assets = useAppSelector(registryAssetsModel.selectors.selectItems);
  const isLoading = useAppSelector(
    registryAssetsModel.selectors.selectIsLoading,
  );
  const error = useAppSelector(registryAssetsModel.selectors.selectError);

  const [searchQuery, setSearchQuery] = useState('');
  const [importingId, setImportingId] = useState<string | null>(null);

  const isSupportedFamily = family === 'document_template';

  useEffect(() => {
    if (!isOpen || !isSupportedFamily || !organizationId) {
      return;
    }

    const promise = dispatch(
      registryAssetsModel.thunks.fetchRegistryAssets({
        organizationId,
        family,
        language,
        limit: REGISTRY_SELECTOR_PAGE_SIZE,
      }),
    );

    return () => {
      promise.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family, isOpen, isSupportedFamily, language, organizationId]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setImportingId(null);
    }
  }, [isOpen]);

  const filteredAssets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return assets;
    }

    return assets.filter((asset) => {
      const haystack = [asset.name, asset.description ?? '']
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [assets, searchQuery]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleImport = async (asset: RegistryAsset) => {
    if (!organizationId || importingId) {
      return;
    }

    setImportingId(asset.id);

    try {
      const result = await templatesApi.cloneDocumentTemplate(asset.id, {
        target_organization_id: organizationId,
      });

      void dispatch(
        templatesModel.fetchDocumentTemplates({
          organization_id: organizationId,
        }),
      );

      toastCaller({
        type: 'success',
        message: formatMessage(i18n.communityTemplatesMessages.importSuccess),
      });

      onSelect({
        family,
        document_template_id: result.local_asset_id,
        document_template_series_id: result.local_series_id,
      });
      onClose();
    } catch (error) {
      toastCaller({
        type: 'error',
        message:
          error instanceof Error && error.message
            ? error.message
            : formatMessage(i18n.communityTemplatesMessages.importError),
      });
    } finally {
      setImportingId(null);
    }
  };

  const renderBody = () => {
    if (!isSupportedFamily) {
      return (
        <div className={styles.unsupported}>
          {formatMessage(
            i18n.communityTemplatesMessages.registrySelectorUnsupportedFamily,
          )}
        </div>
      );
    }

    // The store keeps the previous answer through the next request and past its
    // failure, so these two come before the cards — otherwise stale rows read as
    // the catalog's current contents.
    if (isLoading) {
      return (
        <div className={styles.empty}>
          <FormattedMessage id="global.loading" defaultMessage="Loading…" />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.empty}>
          <FormattedMessage {...toastErrors.defaultError} />
        </div>
      );
    }

    if (filteredAssets.length === 0) {
      return (
        <div className={styles.empty}>
          {formatMessage(i18n.communityTemplatesMessages.registrySelectorEmpty)}
        </div>
      );
    }

    return (
      <div className={styles.grid}>
        {filteredAssets.map((asset) => (
          <RegistryAssetSelectorCard
            key={asset.id}
            asset={asset}
            disabled={Boolean(importingId) && importingId !== asset.id}
            onClick={handleImport}
          />
        ))}
      </div>
    );
  };

  return (
    <Modal
      data-component="registry-asset-selector-dialog"
      isOpen={isOpen}
      title={formatMessage(
        i18n.communityTemplatesMessages.registrySelectorTitle,
      )}
      onCancel={onClose}
      hideFooter
      bodyClassName={styles.body}
    >
      <div className={styles.container}>
        {isSupportedFamily ? (
          <Input
            name="registrySelectorSearch"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={formatMessage(
              i18n.communityTemplatesMessages.registrySelectorSearchPlaceholder,
            )}
            autoFocus
            disabled={Boolean(importingId)}
          />
        ) : null}

        {importingId ? (
          <div className={cn('p3', styles.importingBanner)}>
            {formatMessage(
              i18n.communityTemplatesMessages.registrySelectorImporting,
            )}
          </div>
        ) : null}

        {renderBody()}
      </div>
    </Modal>
  );
};
